import { createContext, FunctionComponent, isValidElement, useCallback, useContext, useMemo, useState } from "react";
import { useShallowEffect } from "@mantine/hooks";
import { Form, Formik, FormikProps, FormikValues, useFormikContext } from "formik";
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import styles from "./stepped-modal.module.scss";
import type {
    CustomSteppedModalFactory,
    IProps,
    IStepSchema,
    ISteppedModalFormikContext,
    ISteppedModalFormikProps,
    ISteppedModalFormikProviderProps,
    ISteppedModalHeaderProps,
} from "./stepped-modal.types";
import { clampStepIndex, getFooter, getFormikSteps, getSteps, isStepSchema } from "./stepped-modal.utilities";
import { componentWrapper } from "../../utilities";
import { Modal } from "../modal";
import { Stepper } from "../stepper";
import { Button } from "../button";
import { SteppedModalStep } from "./stepped-modal-step";
import { SteppedModalFooter } from "./stepped-modal-footer";
import { SteppedModalStepScope } from "./stepped-modal-step-scope";
import { SteppedModalFormikStep } from "./stepped-modal-formik-step";

/**
 * Shared modal header for both the base and Formik SteppedModal variants: the step indicator and
 * the close button. Clicking a previous step navigates back; future steps are not clickable.
 */
const SteppedModalHeader: FunctionComponent<ISteppedModalHeaderProps> = ({
    active,
    onStepChange,
    steps,
    closeButtonDisabled = false,
}) => (
    <Modal.Header className={styles["header"]}>
        <Stepper
            active={active}
            onStepClick={(step) => {
                if (step < active) {
                    onStepChange(step);
                }
            }}
            allowNextStepsSelect={false}
            size="sm"
            className={styles["stepper"]}
        >
            {steps.map((step, index) => (
                <Stepper.Step key={index} label={step.props.label} />
            ))}
        </Stepper>
        <Modal.CloseButton disabled={closeButtonDisabled} />
    </Modal.Header>
);

/**
 * Modal with multi-step navigation. The header shows a step indicator (numbered pills); only the
 * active step's content is rendered in the body. Fully controlled via `active`/`onStepChange`.
 * Clicking a previous step navigates back; future steps are not clickable.
 */
const SteppedModal = componentWrapper<CustomSteppedModalFactory, IProps>(
    "SteppedModal",
    ({ active, onStepChange, children, closeButtonDisabled, ...otherProps }, ref) => {
        const steps = getSteps(children);
        const footer = getFooter(children);
        const clampedActive = clampStepIndex(active, steps.length);

        return (
            <Modal.Root ref={ref} size="xl" {...otherProps}>
                <Modal.Overlay />
                <Modal.Content>
                    <SteppedModalHeader
                        active={clampedActive}
                        onStepChange={onStepChange}
                        steps={steps}
                        closeButtonDisabled={closeButtonDisabled}
                    />
                    <Modal.Body>{steps[clampedActive]}</Modal.Body>
                    {footer}
                </Modal.Content>
            </Modal.Root>
        );
    }
);

const SteppedModalFormikContext = createContext<ISteppedModalFormikContext>({ setFormikProps: () => {} });

const SteppedModalFormikProvider: FunctionComponent<ISteppedModalFormikProviderProps> = ({
    children,
    setFormikProps,
}) => <SteppedModalFormikContext.Provider value={{ setFormikProps }}>{children}</SteppedModalFormikContext.Provider>;

/**
 * Rendered inside the owned <Form> to push the live Formik state up to the footer buttons, which sit
 * outside the form (so the modal keeps Mantine's flex layout). Cleared on unmount.
 */
const SteppedModalFormikObserver: FunctionComponent = () => {
    const { setFormikProps } = useContext(SteppedModalFormikContext);
    const values = useFormikContext();
    useShallowEffect(() => {
        setFormikProps(values);
        return () => setFormikProps(null);
    }, [values, setFormikProps]);
    return null;
};

/**
 * Formik-aware SteppedModal. It owns the form: pass the Formik config (initialValues, onSubmit, …) as
 * props and the SteppedModal.Formik.Step children as content, each with its own `validation` schema.
 * The whole-form validation schema is derived by concatenating every step's schema, so there is no
 * separate `validationSchema` prop to drift out of sync with the steps. A single Formik wraps the
 * active step's content inside the modal body, so form state persists while navigating between steps
 * and is reset when the modal closes (the body unmounts). The footer (Back/Next/submit) is built in
 * and driven by the form's validity/submitting state, with `submitButtonDisabled` available for
 * validity Formik cannot see. The modal cannot be dismissed while the form is submitting.
 */
const SteppedModalFormik = <Values extends FormikValues = FormikValues>({
    active,
    onStepChange,
    children,
    submitButtonText = "Save",
    hideSubmitButton = false,
    submitButtonDisabled = false,
    initialValues,
    onSubmit,
    enableReinitialize,
    validateOnMount,
    ...modalProps
}: ISteppedModalFormikProps<Values>) => {
    const [observed, setObserved] = useState<FormikProps<FormikValues> | null>(null);

    // Memoised because this component re-renders from its own `observed` state on every Formik
    // change (each keystroke) while `children` stays referentially stable, so the memo genuinely
    // skips re-parsing the children on those renders. (Not worthwhile in the base variant, which
    // only re-renders when its parent does — i.e. with fresh `children`.)
    const steps = useMemo(() => getFormikSteps(children), [children]);
    const clampedActive = clampStepIndex(active, steps.length);
    const isFinalStep = clampedActive === steps.length - 1;
    const nextButtonText =
        steps[clampedActive]?.props.nextButtonText ?? `Next: ${steps[clampedActive + 1]?.props.label ?? ""}`;
    const isSubmitting = observed?.isSubmitting ?? false;
    const isValid = observed?.isValid ?? false;

    const [registeredSchemas, setRegisteredSchemas] = useState<Record<number, IStepSchema | undefined>>({});
    // Bail out when nothing changed: a slot re-registering the same schema must not loop through state.
    const registerValidation = useCallback((index: number, schema: IStepSchema | undefined) => {
        setRegisteredSchemas((previous) =>
            index in previous && previous[index] === schema ? previous : { ...previous, [index]: schema }
        );
    }, []);

    // One schema per step: the prop's when it is static, the slot's registration when it is an element.
    // `validation` is typed as required, but the type system behind that guarantee never actually runs
    // here (no typecheck target, no `tsc` build, esbuild/swc strip types at transpile time) — so an
    // untyped or careless caller CAN reach this at runtime with `validation` omitted or malformed. Guard
    // structurally rather than trusting the cast: a value that is neither a valid element nor a real
    // schema becomes `undefined` (fails closed, see `isValidationReady`/`isActiveStepValid` below)
    // instead of being passed through to crash later on `.isValidSync` (e.g. `validation={null}`) or on
    // `.concat` while composing the whole-form schema (a half-schema carrying only `isValidSync`).
    const stepSchemas = useMemo(
        () =>
            steps.map((step, index): IStepSchema | undefined => {
                const { validation } = step.props;
                if (isValidElement(validation)) {
                    return registeredSchemas[index];
                }
                if (isStepSchema(validation)) {
                    return validation;
                }
                if (process.env.NODE_ENV !== "production") {
                    console.error(
                        `SteppedModal.Formik.Step "${step.props.label}" (index ${index}) has an invalid ` +
                            "`validation` prop: expected a Yup-like schema or a registering element. Next and " +
                            "submit will stay disabled for this step until it declares one."
                    );
                }
                return undefined;
            }),
        [steps, registeredSchemas]
    );

    // The whole-form schema IS the steps' schemas, so per-step gating and submit gating cannot disagree.
    // No seed: an empty reduce would need a schema constant, which would mean depending on yup here.
    // `undefined` is a valid Formik validationSchema, and the readiness gate below stops that state being
    // actionable.
    const validationSchema = useMemo(() => {
        const declared = stepSchemas.filter((schema): schema is IStepSchema => typeof schema !== "undefined");
        return declared.length === 0 ? undefined : declared.reduce((accumulated, schema) => accumulated.concat(schema));
    }, [stepSchemas]);

    const values = observed?.values;
    const activeStepSchema = stepSchemas[clampedActive];
    // A later step's errors must not block progress through the current one. Validating the whole values
    // object against one step's schema works because unknown keys are allowed, and `.when()` referring to
    // another step's field still resolves against the parent value object.
    //
    // `activeStepSchema` is undefined for two different reasons, which must NOT be treated alike.
    // When there are no steps at all there is no active step to speak of, so fall back to the whole
    // form's `isValid` for that harmless edge case (and the Next button is suppressed entirely then
    // anyway — see `isFinalStep` below). Every other case — a registering element that has not
    // reported yet, a static `validation` left `undefined` by an unchecked caller, or any value that
    // failed the runtime schema check above — must block Next outright rather than loosen to
    // whichever way the whole form happens to read. `validation` being a required prop does NOT make
    // "nobody declared this step's rules" unreachable at runtime: this repo has no typecheck target,
    // `tsc -p` cannot run at all, and both CI (esbuild) and Jest (`@swc/jest`) transpile without ever
    // checking types, so a step that forgot `validation` reaches here at runtime exactly like before
    // this design — and falling back to `isValid` here is what reproduced that original dead-end.
    const isActiveStepValid = useMemo(() => {
        if (typeof activeStepSchema !== "undefined") {
            return activeStepSchema.isValidSync(values ?? {});
        }
        return steps.length === 0 ? isValid : false;
    }, [activeStepSchema, isValid, values, steps.length]);

    // Formik's validate callback is a useEventCallback — useCallback(fn, []) — so its mount-validation
    // effect never re-runs and nothing re-validates when validationSchema changes identity. Do it here so
    // no caller has to remember.
    //
    // Depends on the steps' schemas, compared shallowly, rather than on the composed `validationSchema`:
    // that object is rebuilt whenever `stepSchemas` is, and `stepSchemas` derives from `children`, which
    // are fresh elements on every render of the caller — so its identity changed on every step change
    // even though no step's rules had. Re-validating there was actively harmful, not just wasted work:
    // `validateForm()` validates whatever values Formik holds when it is called, and an arriving step's
    // own mount effect can update a value in the same commit (the model form's Sensitivity step prefills
    // its threshold from the resolved floor). Child effects run before the parent's, so this call was
    // issued last, resolved last, and its pre-update result overwrote the fresher one — leaving submit
    // disabled on an already-valid form until a blur re-validated it.
    const validateForm = observed?.validateForm;
    useShallowEffect(() => {
        void validateForm?.();
    }, [validateForm, stepSchemas]);

    // Registration lands in an effect, so on the first frame nothing has registered and an incomplete
    // composed schema would read as a valid form. Submit validates the WHOLE form, so it must stay
    // disabled until every step has a schema — unlike Next, which only ever needs the active step's own
    // readiness (folded into `isActiveStepValid` above) because a later step's rules must never block
    // progress through an earlier one. Checking every step (not just the ones known to register via a
    // slot) also covers a step whose `stepSchemas` entry is `undefined` for any other reason — a static
    // `validation` left undefined, or an element that never calls `useStepValidation` — so submit stays
    // blocked rather than reading an incomplete composed schema as valid.
    const isValidationReady = stepSchemas.every((schema) => typeof schema !== "undefined");

    return (
        <Modal.Root size="xl" closeOnClickOutside={!isSubmitting} closeOnEscape={!isSubmitting} {...modalProps}>
            <Modal.Overlay />
            <Modal.Content>
                <SteppedModalFormikProvider setFormikProps={setObserved}>
                    <SteppedModalHeader
                        active={clampedActive}
                        onStepChange={onStepChange}
                        steps={steps}
                        closeButtonDisabled={isSubmitting}
                    />
                    <Modal.Body>
                        <Formik<Values>
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={onSubmit}
                            enableReinitialize={enableReinitialize}
                            validateOnMount={validateOnMount}
                        >
                            <Form>
                                <SteppedModalFormikObserver />
                                {steps.map((step, index) => (
                                    <SteppedModalStepScope
                                        key={index}
                                        index={index}
                                        isActive={index === clampedActive}
                                        registerValidation={registerValidation}
                                    >
                                        {/* Rendered for every step, active or not, so the whole-form schema is
                                            complete before the user has visited each step. Renders null. */}
                                        {isValidElement(step.props.validation) && step.props.validation}
                                        {step}
                                    </SteppedModalStepScope>
                                ))}
                            </Form>
                        </Formik>
                    </Modal.Body>
                    <SteppedModalFooter>
                        {active > 0 && (
                            <Button
                                variant="default"
                                icon={faChevronLeft}
                                onClick={() => onStepChange(active - 1)}
                                disabled={isSubmitting}
                                data-testid="stepped-modal-back-button"
                            >
                                Back
                            </Button>
                        )}
                        {/* With zero recognised steps, `isFinalStep` (`0 === -1`) is false, so without this
                            guard an empty-labelled Next would render for a modal with no
                            SteppedModal.Formik.Step children (e.g. base SteppedModal.Step children passed
                            here by mistake — getFormikSteps filters them all out). */}
                        {!isFinalStep && steps.length > 0 && (
                            <Button
                                variant="filled"
                                onClick={() => onStepChange(active + 1)}
                                disabled={observed === null || !isActiveStepValid || isSubmitting}
                                data-testid="stepped-modal-next-button"
                                rightIcon={faChevronRight}
                            >
                                {nextButtonText}
                            </Button>
                        )}
                        {isFinalStep && !hideSubmitButton && (
                            <Modal.Footer.Actions.Done
                                text={submitButtonText}
                                onClick={() => observed?.submitForm()}
                                loading={isSubmitting}
                                disabled={
                                    observed === null ||
                                    !isValid ||
                                    isSubmitting ||
                                    submitButtonDisabled ||
                                    !isValidationReady
                                }
                            />
                        )}
                    </SteppedModalFooter>
                </SteppedModalFormikProvider>
            </Modal.Content>
        </Modal.Root>
    );
};

SteppedModal.Step = SteppedModalStep;
SteppedModal.Footer = SteppedModalFooter;
SteppedModal.Formik = SteppedModalFormik;

// Attached after declaration rather than inline because SteppedModalFormik is a generic function
// component, so it cannot carry statics in its own type annotation.
SteppedModalFormik.Step = SteppedModalFormikStep;

export { SteppedModal, SteppedModalFormik };
